import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ExportFormats, ExportItem, ExportItems, TypeFormats } from '../types/exportItem';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

/*
** Default values, we will use the same title, langkey and icon if there is no values in the params
*/
type ExportDefault<T> = Required<Omit<ExportItem<T>, 'getData'>>;

type ExportItemsDefault = {
    [key in TypeFormats]: ExportDefault<key>;
};
export const EXPORT_DEFAULT_ITEMS: ExportItemsDefault = {
    excel: {
        title: 'Excel Spreadsheet (.XLSX)',
        langKey: 'excelSpreadsheet',
        icon: "../App_Themes/clearview-default/icons/utility/excel-16px.svg#excel-16px",
        format: 'excel',
    },
    csv: {
        title: 'Generic Spreadsheet (.CSV)',
        langKey: 'csvSpreadsheet',
        icon: "../App_Themes/clearview-default/icons/utility/csv.svg#csv",
        format: 'csv',
    },
    png: {
        title: 'PNG',
        langKey: 'pngFile',
        icon: "../App_Themes/clearview-default/icons/dashboard/image.svg#image",
        format: 'png',
    }
};

// Current supported formats, if you need to add a new format you should first add it in the type ExportFormats
const EXPORT_FORMAT: ExportFormats = {
    csv: {
        description: 'CSV',
        mime: 'text/csv',
        extension: 'csv',
    },
    excel: {
        description: 'Excel',
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extension: 'xlsx',
    },
    png: {
        description: 'png',
        mime: 'image/png',
        extension: 'png',
    },
};

@Component({
    selector: 'cv-generic-export',
    templateUrl: './generic-export.component.html',
    styleUrls: ['./generic-export.component.scss'],
    inputs: ['items', 'fileName'],
})
export class GenericExportComponent implements OnInit {

    @ViewChild(NgbDropdown) dropdown: NgbDropdown;

    @Input() items: ExportItems = {};

    @Input() fileName = 'filename';

    @Input() disabled = false;

    itemsToDisplay: Required<ExportItem<TypeFormats>>[] = [];

    constructor() { }

    ngOnInit(): void {
        this.itemsToDisplay = Object.entries(this.items)
            .map(([key, values]: [TypeFormats, ExportItem<TypeFormats>]) => ({
                ...EXPORT_DEFAULT_ITEMS[key], // load the default data
                ...values, // override with the new title, langKey, and so on if exists
            }));
    }

    async onClickDownloadFile(format: TypeFormats) {
        if (!this.items[format]) return; // if there is no format then we don't want to do anything
        // close menu
        this.dropdown.close();
        const data = await this.items[format].getData(format);
        const formatDetails = EXPORT_FORMAT[format];

        const regex = /^data:image\/png;base64,/;
        const currentFileName = `${this.fileName}${formatDetails.description}_${(new Date()).toString('yyyymmddhhmmss')}`;

        if (data) {
            if (regex.test(data)) {
                this.downloadPNG(data, `${currentFileName}.${formatDetails.extension}`)
            } else {
                this.downloadFile(data, formatDetails.mime, `${currentFileName}.${formatDetails.extension}`);
            }

        }
    }

    /**
     * Downloads a file from a byte array string
     *
     * @param data The byte[] string returned from the endpoint
     * @param mime The mime type to mark the file as
     * @param fileName The name of the file to be downloaded
     */
    downloadFile(data: string, mime: string, fileName: string) {
        const decodedData = atob(data);
        const buffer = new ArrayBuffer(decodedData.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i !== decodedData.length; ++i) {
            // eslint-disable-next-line no-bitwise
            view[i] = decodedData.charCodeAt(i) & 0xFF;
        }
        const fileData = [buffer];
        const blobObject = new Blob(fileData, { type: mime });
        // create fake link
        const linkElement = document.createElement('a');
        document.body.appendChild(linkElement);
        linkElement.setAttribute('style', 'display: none;');
        const url = URL.createObjectURL(blobObject);
        linkElement.href = url;
        linkElement.download = fileName;
        // start download
        linkElement.click();
        // cleanup
        URL.revokeObjectURL(linkElement.href);
        linkElement.remove();
    };

    /**
     * Downloads a PNG file directly from a data URI
     *
     * @param data The data URI of the PNG file
     * @param fileName The name of the file to be downloaded
     */
    downloadPNG(data: string, fileName: string) {
        // create fake link
        const link = document.createElement('a');
        link.href = data;
        link.download = fileName;

        // start download
        link.click();
        
        // cleanup
        URL.revokeObjectURL(link.href);
        link.remove();
    };
}
